import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, Eye, EyeOff, KeyRound, Mail, MailWarning, X } from 'lucide-react';
import { Button } from './ui/Button';

/**
 * The one-time temporary password, shown until it is deliberately dismissed.
 *
 * It used to arrive as a toast, which auto-dismissed after a few seconds — long enough to read a
 * confirmation, nowhere near long enough to copy a password into an e-mail, read it down a phone
 * line, or write it on a card for a customer standing at the counter. Miss it and the only way
 * back is another reset.
 *
 * So it stays on screen, with a copy button, and Dismiss is disabled for the first minute:
 * whoever provisioned the account cannot close it by reflex before they have used it. It never
 * auto-hides — the operator decides when the password has been passed on, because only they know
 * whether it has.
 *
 * The server also emails the password to the account holder; `emailSent` / `emailError` say
 * whether that worked, so the operator knows whether handing it over by hand is still needed.
 * The eye toggle masks it when someone else is looking at the screen — copy works either way.
 */
export function TempPasswordPanel({
  email,
  password,
  minSeconds = 60,
  onDismiss,
  emailSent,
  emailError,
  audience = 'customer',
}: {
  email: string;
  password: string;
  emailSent?: boolean;
  emailError?: string;
  /** Who the password is passed to — wording only. */
  audience?: 'customer' | 'staff';
  /** How long Dismiss stays disabled. The point is to outlast a reflex, not to lock anyone in. */
  minSeconds?: number;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const [left, setLeft] = useState(minSeconds);
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(true);

  // One countdown per password: a second reset restarts it, because it is a different secret.
  useEffect(() => {
    setLeft(minSeconds);
    setCopied(false);
    setShown(true);
    const id = setInterval(() => setLeft((n) => (n <= 1 ? 0 : n - 1)), 1000);
    return () => clearInterval(id);
  }, [password, minSeconds]);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard permission (or an insecure origin): the password is on screen in a
      // selectable field, which is the fallback that always works.
      setCopied(false);
    }
  };

  return (
    <div className="mb-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
          <KeyRound className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h4 className="font-semibold">{t('adminScreens.tempPassword.title', { email })}</h4>
          <p className="mt-0.5 text-sm text-muted-foreground">
            {audience === 'staff'
              ? t('adminScreens.tempPassword.introStaff')
              : t('adminScreens.tempPassword.introCustomer')}
          </p>
          {emailSent === true && (
            <p className="mt-2 flex items-center gap-1.5 text-sm text-success">
              <Mail className="h-4 w-4" /> {t('adminScreens.tempPassword.emailed', { email })}
            </p>
          )}
          {emailSent === false && (
            <p className="mt-2 flex items-start gap-1.5 text-sm text-warning">
              <MailWarning className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                <strong>{t('adminScreens.tempPassword.notEmailed')}</strong>
                {emailError ? ` — ${emailError}` : ''} {t('adminScreens.tempPassword.passItOn')}
              </span>
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {/* Read-only rather than plain text: it can be selected, and on a phone it can be
                long-pressed, on every browser where the clipboard API is unavailable. */}
            <input
              readOnly
              type={shown ? 'text' : 'password'}
              value={password}
              aria-label={t('adminScreens.tempPassword.ariaPassword')}
              onFocus={(e) => e.currentTarget.select()}
              className="h-9 min-w-[16rem] flex-1 rounded-md border border-input bg-background px-3 font-mono text-sm tracking-wider shadow-sm"
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShown((v) => !v)}
              aria-label={
                shown
                  ? t('adminScreens.tempPassword.hidePassword')
                  : t('adminScreens.tempPassword.showPassword')
              }
              aria-pressed={!shown}
            >
              {shown ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              {shown ? t('adminScreens.tempPassword.hide') : t('adminScreens.tempPassword.show')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => void copy()}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              {copied ? t('adminScreens.tempPassword.copied') : t('adminScreens.tempPassword.copy')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onDismiss} disabled={left > 0}>
              <X className="h-4 w-4" />
              {left > 0
                ? t('adminScreens.tempPassword.dismissIn', { seconds: left })
                : t('adminScreens.tempPassword.dismiss')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
